import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress,
  IconButton,
  Typography,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { X, Edit3, Save } from "lucide-react";
import Axios from "../../../Common/api/axiosConfig";

interface EditTenantModalProps {
  open: boolean;
  onClose: () => void;
  tenant: any;
  onSaved: () => void;
}

export const EditTenantModal: React.FC<EditTenantModalProps> = ({
  open,
  onClose,
  tenant,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    url: "",
    number: "",
    address: "",
    description: "",
    operating_hours: "",
    delivery: "",
    visibility: false,
    status: "active",
  });

  useEffect(() => {
    if (tenant) {
      setFormData({
        name: tenant.store?.name || "",
        url: tenant.store?.url || "",
        number: tenant.store?.number ? String(tenant.store.number) : "",
        address: tenant.store?.address || "",
        description: tenant.store?.description || "",
        operating_hours: tenant.store?.operating_hours || "",
        delivery: tenant.store?.delivery || "",
        visibility: tenant.store?.visibility ?? true,
        status: tenant.status || "active",
      });
      setError(null);
    }
  }, [tenant]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleStatusChange = (val: string) => {
    setFormData((prev) => ({
      ...prev,
      status: val,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenant) return;

    setLoading(true);
    setError(null);

    try {
      // 1. Update user status if changed
      if (formData.status !== tenant.status) {
        await Axios.patch(`/admin/tenants/${tenant._id}/status`, {
          status: formData.status,
          storeVisibility: formData.visibility,
        });
      }

      // 2. Update store settings if store exists
      if (tenant.store?._id || tenant.store?.name) {
        await Axios.patch(`/admin/tenants/${tenant._id}/store`, {
          name: formData.name,
          url: formData.url.trim().toLowerCase(),
          number: Number(formData.number) || formData.number,
          address: formData.address,
          description: formData.description,
          operating_hours: formData.operating_hours,
          delivery: formData.delivery,
          visibility: formData.visibility,
          status: formData.status,
        });
      }

      onSaved();
      onClose();
    } catch (err: any) {
      console.error("Error updating tenant:", err);
      setError(err?.response?.data?.message || "Failed to save tenant changes");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Edit3 size={20} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Edit Tenant Settings
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {error && (
            <Typography
              variant="body2"
              color="error.main"
              sx={{ mb: 2, p: 1, backgroundColor: "error.light", borderRadius: 1 }}
            >
              {error}
            </Typography>
          )}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Owner Email (Read-only)"
                value={tenant?.email || ""}
                disabled
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Store Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Store URL / Domain Slug"
                name="url"
                value={formData.url}
                onChange={handleChange}
                helperText="e.g. therudrix.in or my-store"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="WhatsApp Phone Number"
                name="number"
                value={formData.number}
                onChange={handleChange}
                placeholder="e.g. 919876543210"
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-label">Account Status</InputLabel>
                <Select
                  labelId="status-label"
                  label="Account Status"
                  value={formData.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Address / Location"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Operating Hours"
                name="operating_hours"
                value={formData.operating_hours}
                onChange={handleChange}
                placeholder="e.g. 10:00 AM - 8:00 PM"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Delivery Info"
                name="delivery"
                value={formData.delivery}
                onChange={handleChange}
                placeholder="e.g. Pan-India Delivery"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                label="Store Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.visibility}
                    onChange={handleChange}
                    name="visibility"
                    color="primary"
                  />
                }
                label="Store Online & Visible to Public"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{ p: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            size="small"
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="small"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <Save size={16} />}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditTenantModal;
